import type { ProgrammeStatus, StaffRole } from '@/api/types';

const RANK: Record<StaffRole, number> = { editor: 1, approver: 2, admin: 3 };

export function atLeast(role: StaffRole | undefined, min: StaffRole): boolean {
  return !!role && RANK[role] >= RANK[min];
}

export type Capability =
  | 'edit'
  | 'approve'
  | 'publish'
  | 'previewApprove'
  | 'editTaxonomy'
  | 'editHomeLayouts'
  | 'manageStaff';

const MIN_ROLE: Record<Capability, StaffRole> = {
  edit: 'editor',
  approve: 'approver',
  publish: 'approver',
  previewApprove: 'approver',
  editTaxonomy: 'approver',
  editHomeLayouts: 'approver',
  manageStaff: 'admin',
};

export function can(role: StaffRole | undefined, cap: Capability): boolean {
  return atLeast(role, MIN_ROLE[cap]);
}

export interface TransitionDef {
  to: ProgrammeStatus;
  label: string;
  minRole: StaffRole;
  /** Blocked while the programme has validation issues (06 §3.1). */
  needsValidation: boolean;
  /** Primary (forward) step vs. a step back. */
  primary: boolean;
  confirm?: string;
}

/** The workflow of 06 §3, keyed by the current status. */
export const TRANSITIONS: Record<ProgrammeStatus, TransitionDef[]> = {
  draft: [
    {
      to: 'in_review',
      label: 'Submit for review',
      minRole: 'editor',
      needsValidation: false,
      primary: true,
    },
  ],
  in_review: [
    { to: 'approved', label: 'Approve', minRole: 'approver', needsValidation: true, primary: true },
    {
      to: 'draft',
      label: 'Send back to draft',
      minRole: 'editor',
      needsValidation: false,
      primary: false,
    },
  ],
  approved: [
    {
      to: 'published',
      label: 'Publish',
      minRole: 'approver',
      needsValidation: true,
      primary: true,
      confirm: 'Publishing makes this programme visible to children whose settings allow it.',
    },
    {
      to: 'draft',
      label: 'Back to draft',
      minRole: 'editor',
      needsValidation: false,
      primary: false,
    },
  ],
  published: [
    {
      to: 'archived',
      label: 'Archive',
      minRole: 'approver',
      needsValidation: false,
      primary: false,
      confirm:
        'Archiving withdraws the programme from the app straight away. Nothing is deleted; it can be restored to draft later.',
    },
  ],
  archived: [
    {
      to: 'draft',
      label: 'Restore to draft',
      minRole: 'approver',
      needsValidation: false,
      primary: true,
    },
  ],
};

export const STATUS_LABEL: Record<ProgrammeStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
};

export const ROLE_LABEL: Record<StaffRole, string> = {
  editor: 'Editor',
  approver: 'Approver',
  admin: 'Admin',
};
